import { z } from 'zod';
import { decimalStringSchema } from './primitives';

/**
 * The mining wire.
 *
 * ## What the client may say
 *
 * An instance id, and for a collection a replay key. That is the whole of it.
 * Every request schema is `.strict()`, so Power, production, a balance, a
 * timestamp or an account cannot be smuggled in even by accident - the server
 * knows all of them and would not believe this side about any of it.
 *
 * ## What the client is told
 *
 * Whole amounts as decimal STRINGS, never JSON numbers: ore is a balance in
 * micro-units and a double would lose it. Power likewise - it is summed as a
 * `bigint` on the server and must survive the trip.
 */

/** Why a ship cannot mine. One reason, and all of them are safe to show. */
export const MINING_INELIGIBLE_REASONS = [
  /** It is the ship the account is flying. */
  'equipped',
  /** Already docked in this operation. */
  'mining',
  /** Held by a listing, a direct trade or a merge. */
  'reserved',
  /** Not owned, consumed or retired. */
  'unavailable',
] as const;

export type MiningIneligibleReason = (typeof MINING_INELIGIBLE_REASONS)[number];

/** One ship the account owns, and whether it may be docked. */
export const miningShipOptionSchema = z
  .object({
    instanceId: z.string().min(1),
    definitionId: z.string().min(1),
    /** For the thumbnail. The same asset id the vault already uses. */
    assetKey: z.string().min(1),
    name: z.string().min(1),
    rarity: z.string().min(1),
    shipIndex: z.number().int().min(1),
    level: z.number().int().min(1),
    /** Derived from index and level on the server. Never stored, never sent in. */
    power: decimalStringSchema,
    /** True when this ship is docked right now. */
    assigned: z.boolean(),
    eligible: z.boolean(),
    /** Null exactly when `eligible`. */
    reason: z.enum(MINING_INELIGIBLE_REASONS).nullable(),
  })
  .strict();

/**
 * The planet the fleet is breaking, as the SERVER describes it.
 *
 * Read-only in every sense: there is no request that carries a health, a tier,
 * a scale or a reward, and this shape has no field a client could send back. A
 * screen that could name its own planet's health would be a screen that could
 * name a smaller one.
 *
 * `potentialGold` is what a confirmed destruction pays. The rare drops are
 * deliberately NOT enumerated here - the odds are public in the design notes,
 * but naming the exact roll before the planet breaks would turn a reward into
 * a countdown.
 */
export const miningPlanetSchema = z
  .object({
    planetId: z.string().min(1),
    tier: z.number().int().min(1).max(6),
    ordinal: z.number().int().min(1),
    /** Whole health points, as decimal strings: these are exact bigints. */
    remainingHealth: decimalStringSchema,
    maxHealth: decimalStringSchema,
    /** What the fleet removes per second. Zero with nothing docked. */
    damagePerSecond: decimalStringSchema,
    /** Whole seconds until it breaks at the current rate, or null. */
    secondsToDestroy: decimalStringSchema.nullable(),
    /** Presentation: how large it is drawn, and in what colour. */
    visualScaleBps: z.number().int().min(1),
    maxStageFractionBps: z.number().int().min(1),
    palette: z.string().min(1),
    /** Gold a confirmed destruction guarantees. */
    potentialGold: decimalStringSchema,
    /** How many independent drop positions the destruction will roll. */
    dropPositions: z.number().int().min(0),
  })
  .strict();

export type MiningPlanet = z.infer<typeof miningPlanetSchema>;

export const miningStateSchema = z
  .object({
    operationId: z.string().nullable(),
    state: z.enum(['active', 'cancelled', 'none']),
    /** The docked fleet, in a stable order the scene lays out from. */
    ships: z.array(miningShipOptionSchema),
    /** Every ship the account owns, docked or not, with its verdict. */
    fleet: z.array(miningShipOptionSchema),
    totalPower: decimalStringSchema,
    oreMicroPerSecond: decimalStringSchema,
    storedOreMicro: decimalStringSchema,
    capacityMicro: decimalStringSchema,
    /** ISO instant of the last settlement, or null before there was one. */
    lastSettledAt: z.string().nullable(),
    /**
     * The SAFETY limit on the fleet, and nothing more.
     *
     * It was twelve, which was the size of one ring - a drawing decision
     * wearing an economic rule's clothes. The rings grow now, so this is a
     * hundred and the interface says "N/100" because that is what it is.
     */
    maxShips: z.number().int().min(1),
    /** Null only before the first planet exists. */
    planet: miningPlanetSchema.nullable(),
  })
  .strict();

export type MiningShipOption = z.infer<typeof miningShipOptionSchema>;
export type MiningState = z.infer<typeof miningStateSchema>;

/** Docking or undocking a ship. An id, and nothing else. */
export const miningShipRequestSchema = z
  .object({
    instanceId: z.string().min(1),
  })
  .strict();

export type MiningShipRequest = z.infer<typeof miningShipRequestSchema>;

/**
 * Collecting.
 *
 * A replay key and nothing else: the AMOUNT is whatever the server has, and a
 * request that named one would be a request that could name a bigger one.
 */
export const miningCollectRequestSchema = z
  .object({
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();

export type MiningCollectRequest = z.infer<typeof miningCollectRequestSchema>;

export const miningCollectResponseSchema = z
  .object({
    state: miningStateSchema,
    /** What this call moved out. Zero on a replay is impossible: it repeats. */
    collectedMicro: decimalStringSchema,
  })
  .strict();

export type MiningCollectResponse = z.infer<typeof miningCollectResponseSchema>;

/** Cancelling takes no body at all: there is nothing to say. */
export const miningStateResponseSchema = miningStateSchema;

export const MINING_STATE_ROUTE = '/api/mining' as const;
export const MINING_ASSIGN_ROUTE = '/api/mining/assign' as const;
export const MINING_REMOVE_ROUTE = '/api/mining/remove' as const;
export const MINING_COLLECT_ROUTE = '/api/mining/collect' as const;
export const MINING_CANCEL_ROUTE = '/api/mining/cancel' as const;
